import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Success checkmark draw animation
 */
const drawCheckmark = keyframes`
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

/**
 * Success circle scale animation
 */
const scaleCircle = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Fade in animation
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeIn} 300ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SuccessSVG = styled.svg`
  width: 80px;
  height: 80px;
  margin-bottom: 1rem;

  circle {
    animation: ${scaleCircle} 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
    fill: #28a745;
  }

  path {
    stroke: white;
    stroke-width: 3;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
    animation: ${drawCheckmark} 400ms 200ms cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    circle, path {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
`;

const SuccessMessage = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const SuccessSubtext = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  text-align: center;
`;

interface SuccessFeedbackProps {
  message: string;
  subtext?: string;
}

/**
 * Success Feedback Component
 * Displays animated checkmark with success message
 */
export function SuccessFeedback({ message, subtext }: SuccessFeedbackProps) {
  return (
    <SuccessContainer>
      <SuccessSVG viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="45" />
        <path
          d="M30 50 L45 65 L70 35"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </SuccessSVG>
      <SuccessMessage>{message}</SuccessMessage>
      {subtext && <SuccessSubtext>{subtext}</SuccessSubtext>}
    </SuccessContainer>
  );
}
